import { Component, Input, Output, EventEmitter, OnChanges, OnDestroy, NgZone, ChangeDetectorRef, SimpleChanges } from '@angular/core';

@Component({
    selector: 'initial-import-progress',
    templateUrl: './initial-import-progress.component.html',
    styleUrls: ['./initial-import-progress.component.scss']
})
export class InitialImportProgressComponent implements OnChanges, OnDestroy {
    // The combinations and the graph often take well under a second, so their bars fill no faster than in this time.
    // Each one counts from when its part is seen running, not from when the bar before it is full.
    private static readonly MIN_FILL_TIME_MS = 1500;
    // The last part is reported in thirds, when a default view is done. In between, its bar creeps towards the next third, slower
    // and slower, through at most this share of it, so that it never shows a third done before it is reported.
    private static readonly MAX_CREEP = 0.9;
    // The creep's time constant in the first third (in which it covers 63% of what it may); later thirds take half the duration
    // of the one before.
    private static readonly FIRST_CREEP_TIME_MS = 5000;

    @Input() calcCombosPercent: number = 0;
    @Input() graphPercent: number = 0;
    @Input() defaultViewCreationPercent: number = 0;
    @Input() loading: boolean = false;
    @Input() aborted: boolean = false;
    @Input() error: string | null = null;
    
    @Output() abortClick = new EventEmitter<void>();
    @Output() reloadClick = new EventEmitter<void>();
    // Emitted when all three bars show 100%
    @Output() barsFilled = new EventEmitter<void>();

    shownCalcCombosPercent: number = 0;
    shownGraphPercent: number = 0;
    shownDefaultViewCreationPercent: number = 0;
    private filled: boolean = false;
    private frameId: number | null = null;
    private lastFrameTime: number = 0;
    // The third of the last part that runs: when it was first seen (null while none runs), the percent it started from, and
    // whether it was seen from its start, so that its duration can pace the next third
    private viewsThirdStart: number | null = null;
    private viewsThirdPercent: number = 0;
    private viewsThirdSeenFromStart: boolean = false;
    private viewsCreepTimeMs: number = InitialImportProgressComponent.FIRST_CREEP_TIME_MS;

    constructor(private ngZone: NgZone, private changeDetector: ChangeDetectorRef) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['defaultViewCreationPercent']?.firstChange) {
            if (this.defaultViewCreationPercent >= 100) {
                // Shown after the processing has reported its end, while its job finishes: there is nothing left to watch
                this.shownCalcCombosPercent = this.calcCombosPercent;
                this.shownGraphPercent = this.graphPercent;
                this.shownDefaultViewCreationPercent = this.defaultViewCreationPercent;
            }
            // Shown while the last part already runs, its first third isn't seen from its start
            this.viewsThirdSeenFromStart = this.graphPercent < 100;
        }
        // The frames tell when the bars are filled: emitting here, while the parent's bindings are being checked, would change them
        this.startFrames();
    }

    ngOnDestroy(): void {
        if (this.frameId !== null) {
            cancelAnimationFrame(this.frameId);
        }
    }

    onAbortClick(): void {
        this.abortClick.emit();
    }

    onReloadClick(): void {
        this.reloadClick.emit();
    }

    private startFrames(): void {
        if (this.frameId !== null || this.aborted) {
            return;
        }
        this.lastFrameTime = performance.now();
        // Outside the zone, so that a frame checks only this component and not the cards rendered under the overlay
        this.ngZone.runOutsideAngular(() => {
            this.frameId = requestAnimationFrame(() => this.onFrame());
        });
    }

    private onFrame(): void {
        if (this.aborted) {
            // The bars are hidden; the next change starts the frames again
            this.frameId = null;
            return;
        }
        const now = performance.now();
        const step = 100 * (now - this.lastFrameTime) / InitialImportProgressComponent.MIN_FILL_TIME_MS;
        this.lastFrameTime = now;
        this.trackViewsThird(now);
        const viewsGoal = this.getViewsGoal(now);
        this.shownCalcCombosPercent = Math.min(this.calcCombosPercent, this.shownCalcCombosPercent + step);
        this.shownGraphPercent = Math.min(this.graphPercent, this.shownGraphPercent + step);
        this.shownDefaultViewCreationPercent = Math.min(viewsGoal, this.shownDefaultViewCreationPercent + step);
        this.changeDetector.detectChanges();

        if (this.shownCalcCombosPercent < this.calcCombosPercent || this.shownGraphPercent < this.graphPercent
            || this.shownDefaultViewCreationPercent < viewsGoal || this.viewsThirdStart !== null) {
            this.frameId = requestAnimationFrame(() => this.onFrame());
            return;
        }
        this.frameId = null;
        const wasFilled = this.filled;
        this.filled = this.shownCalcCombosPercent >= 100 && this.shownGraphPercent >= 100 && this.shownDefaultViewCreationPercent >= 100;
        if (this.filled && !wasFilled) {
            this.ngZone.run(() => this.barsFilled.emit());
        }
    }

    // Follows the thirds of the last part
    private trackViewsThird(now: number): void {
        if (this.graphPercent < 100 || this.defaultViewCreationPercent >= 100) {
            this.viewsThirdStart = null;
        } else if (this.viewsThirdStart === null) {
            this.viewsThirdStart = now;
            this.viewsThirdPercent = this.defaultViewCreationPercent;
        } else if (this.defaultViewCreationPercent !== this.viewsThirdPercent) {
            if (this.viewsThirdSeenFromStart && now > this.viewsThirdStart) {
                this.viewsCreepTimeMs = (now - this.viewsThirdStart) / 2;
            }
            this.viewsThirdStart = now;
            this.viewsThirdPercent = this.defaultViewCreationPercent;
            this.viewsThirdSeenFromStart = true;
        }
    }

    // Where the last bar heads: its reported progress, plus the creep while a third runs
    private getViewsGoal(now: number): number {
        if (this.viewsThirdStart === null) {
            return this.defaultViewCreationPercent;
        }
        const creep = 1 - Math.exp((this.viewsThirdStart - now) / this.viewsCreepTimeMs);
        return this.defaultViewCreationPercent + InitialImportProgressComponent.MAX_CREEP * 100 / 3 * creep;
    }
}
